import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import i18n from "../../i18n";

type Language = "en" | "ja";

interface SettingsState {
  language: Language;
  darkMode: boolean;

  // Actions
  setLanguage: (language: Language) => void;
  setDarkMode: (darkMode: boolean) => void;
  toggleDarkMode: () => void;
  toggleLanguage: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      (set) => ({
        language: "ja",
        darkMode: false,

        setLanguage: (language) => {
          set({ language }, false, "settings/setLanguage");
          void i18n.changeLanguage(language);
        },

        setDarkMode: (darkMode) => {
          set({ darkMode }, false, "settings/setDarkMode");
          // Update document class for Tailwind dark mode
          if (darkMode) {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
        },

        toggleDarkMode: () => {
          set(
            (state) => {
              const newDarkMode = !state.darkMode;
              // Update document class for Tailwind dark mode
              if (newDarkMode) {
                document.documentElement.classList.add("dark");
              } else {
                document.documentElement.classList.remove("dark");
              }
              return { darkMode: newDarkMode };
            },
            false,
            "settings/toggleDarkMode",
          );
        },

        toggleLanguage: () => {
          set(
            (state) => {
              const newLanguage = state.language === "en" ? "ja" : "en";
              void i18n.changeLanguage(newLanguage);
              return { language: newLanguage };
            },
            false,
            "settings/toggleLanguage",
          );
        },
      }),
      {
        name: "fivevai-settings-storage",
        // Only persist these fields
        partialize: (state) => ({
          language: state.language,
          darkMode: state.darkMode,
        }),
      },
    ),
    { name: "SettingsStore" },
  ),
);

// Initialize dark mode and language on app start
const initialState = useSettingsStore.getState();
if (initialState.darkMode) {
  document.documentElement.classList.add("dark");
}
void i18n.changeLanguage(initialState.language);
